import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Stack,
  Paper,
  Box,
  Button,
  Tooltip,
  Zoom,
  Grow,
} from "@mui/material";
import { keyframes } from "@mui/system";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import ArticleIcon from "@mui/icons-material/Article";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StraightenIcon from "@mui/icons-material/Straighten";
import CallIcon from "@mui/icons-material/Call";
import ChatIcon from "@mui/icons-material/Chat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import LabelIcon from "@mui/icons-material/Label";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SellIcon from "@mui/icons-material/Sell";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BadgeIcon from "@mui/icons-material/Badge";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const fadeInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// InfoItem Component with PropTypes
const InfoItem = ({ icon, label, value, color }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
    <Box
      sx={{
        color: color,
        mt: 0.3,
        bgcolor: `${color}15`,
        borderRadius: "10px",
        p: 0.8,
        display: "flex",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <MDTypography
        variant="caption"
        color="text.secondary"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="0.5px"
      >
        {label}
      </MDTypography>
      <MDTypography variant="body2" fontWeight="bold" sx={{ wordBreak: "break-word", mt: 0.3 }}>
        {value || "N/A"}
      </MDTypography>
    </Box>
  </Box>
);

InfoItem.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

InfoItem.defaultProps = {
  color: "#666",
  value: "N/A",
};

function UserContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchContactDetails();
  }, [id]);

  const fetchContactDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/user-contact/get-contact?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setContact(response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching contact details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getContactTypeConfig = (type) => {
    switch (type) {
      case "call":
        return {
          gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#667eea",
          icon: <CallIcon sx={{ color: "#fff !important", fontSize: "20px !important" }} />,
          label: "Phone Call",
        };
      case "whatsapp":
        return {
          gradient: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
          color: "#25D366",
          icon: <WhatsAppIcon sx={{ color: "#fff !important", fontSize: "20px !important" }} />,
          label: "WhatsApp Message",
        };
      case "chat":
        return {
          gradient: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
          color: "#2196F3",
          icon: <ChatIcon sx={{ fontSize: 24 }} />,
          label: "Chat Message",
        };
      default:
        return {
          gradient: "linear-gradient(135deg, #9e9e9e 0%, #757575 100%)",
          color: "#9e9e9e",
          icon: <CallIcon sx={{ fontSize: 24 }} />,
          label: type,
        };
    }
  };

  const getStatusChip = (status) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          label="ACTIVE"
          size="small"
          sx={{
            bgcolor: "#E8F5E9",
            color: "#2E7D32",
            fontWeight: "bold",
            fontSize: "0.75rem",
            "& .MuiChip-icon": { color: "#2E7D32 !important" },
          }}
        />
      );
    } else if (status === "pending") {
      return (
        <Chip
          icon={<PendingIcon sx={{ fontSize: 16 }} />}
          label="PENDING"
          size="small"
          sx={{
            bgcolor: "#FFF3E0",
            color: "#E65100",
            fontWeight: "bold",
            fontSize: "0.75rem",
            "& .MuiChip-icon": { color: "#E65100 !important" },
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<CancelIcon sx={{ fontSize: 16 }} />}
          label="INACTIVE"
          size="small"
          sx={{
            bgcolor: "#FFEBEE",
            color: "#C62828",
            fontWeight: "bold",
            fontSize: "0.75rem",
            "& .MuiChip-icon": { color: "#C62828 !important" },
          }}
        />
      );
    }
  };

  const navigateToUser = (userId) => {
    if (userId) {
      navigate(`/users-details/${userId}`);
    }
  };

  const navigateToPost = (postId) => {
    if (postId) {
      navigate(`/post-details/${postId}`);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
          <Box textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #667eea15, #764ba215)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                mb: 2,
                animation: `${float} 2s ease-in-out infinite`,
              }}
            >
              <CircularProgress color="info" size={50} thickness={3} />
            </Box>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
              Loading contact details...
            </MDTypography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!contact) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
          <Zoom in={true}>
            <Card sx={{ p: 5, textAlign: "center", borderRadius: "30px", maxWidth: 450 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "#FFEBEE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                }}
              >
                <CancelIcon sx={{ fontSize: 40, color: "#C62828" }} />
              </Box>
              <MDTypography variant="h5" color="#C62828" fontWeight="bold">
                Contact Not Found
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" mt={1} mb={3}>
                The contact details you&apos;re looking for don&apos;t exist or have been removed.
              </MDTypography>
              <MDButton
                onClick={() => navigate(-1)}
                variant="contained"
                sx={{
                  bgcolor: "#C62828",
                  borderRadius: "12px",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#B71C1C" },
                }}
              >
                Go Back
              </MDButton>
            </Card>
          </Zoom>
        </MDBox>
      </DashboardLayout>
    );
  }

  const contactTypeConfig = getContactTypeConfig(contact.contact_type);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        {/* TOP HEADER */}
        <Box sx={{ animation: `${fadeInUp} 0.6s ease-out`, mb: 3 }}>
          <Card
            sx={{
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                background: contactTypeConfig.gradient,
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -40,
                  left: -20,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ position: "relative", zIndex: 1 }}
              >
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    backdropFilter: "blur(20px)",
                    width: 45,
                    height: 45,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.25)",
                      transform: "translateX(-3px) scale(1.05)",
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <MDTypography variant="h5" fontWeight="bold" color="white">
                    Contact Details
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                    {contactTypeConfig.label} • {formatDate(contact.createdAt)}
                  </MDTypography>
                </Box>
              </Box>
              <Chip
                icon={contactTypeConfig.icon}
                label={contact.contact_type?.toUpperCase()}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  fontWeight: "bold",
                  backdropFilter: "blur(20px)",
                  px: 1,
                  py: 2.5,
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Box>
          </Card>
        </Box>

        <Grid container spacing={3}>
          {/* BUYER CARD */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={600}>
              <Card
                sx={{
                  borderRadius: "24px",
                  overflow: "hidden",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {/* Buyer Header */}
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #1B5E20 0%, #43A047 100%)",
                    p: 3,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -25,
                      right: -25,
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.15)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} position="relative" zIndex={1}>
                    <Avatar
                      src={contact.buyer?.[0]?.profile_photo}
                      sx={{
                        width: 65,
                        height: 65,
                        border: "4px solid  #fff",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                        // animation: `${float} 3s ease-in-out infinite`,
                        bgcolor: "#4CAF50",
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box flex={1}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        {contact.buyer?.[0]?.name || "Unknown Buyer"}
                      </MDTypography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Chip
                          icon={
                            <BadgeIcon
                              sx={{ color: "#fff !important", fontSize: "14px !important" }}
                            />
                          }
                          label="BUYER"
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "0.7rem",
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        {/* {getStatusChip(contact.buyer?.[0]?.status)} */}
                      </Box>
                    </Box>
                    <Tooltip title="View Full Buyer Profile" arrow TransitionComponent={Zoom}>
                      <IconButton
                        onClick={() => navigateToUser(contact.buyer?.[0]?._id)}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.35)",
                            transform: "rotate(360deg)",
                          },
                          transition: "all 0.5s ease",
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Buyer Info */}
                <Box sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    <InfoItem
                      icon={<CorporateFareIcon />}
                      label="Company"
                      value={contact.buyer?.[0]?.company_name}
                      color="#2E7D32"
                    />
                    <InfoItem
                      icon={<EmailIcon />}
                      label="Email Address"
                      value={contact.buyer?.[0]?.email_id}
                      color="#2E7D32"
                    />
                    <InfoItem
                      icon={<PhoneIcon />}
                      label="Mobile Number"
                      value={contact.buyer?.[0]?.mobile_number}
                      color="#2E7D32"
                    />
                    <InfoItem
                      icon={<BusinessIcon />}
                      label="Business Type"
                      value={contact.buyer?.[0]?.business_type}
                      color="#25D366"
                    />
                    <InfoItem
                      icon={<LocationOnIcon />}
                      label="Location"
                      value={`${contact.buyer?.[0]?.city || ""} ${
                        contact.buyer?.[0]?.state ? `, ${contact.buyer?.[0]?.state}` : ""
                      }`}
                      color="#2E7D32"
                    />
                  </Stack>

                  <Divider sx={{ my: 2.5 }} />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigateToUser(contact.buyer?.[0]?._id)}
                    startIcon={<VisibilityIcon />}
                    sx={{
                      bgcolor: "#1B5E20",
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: "bold",
                      py: 1.4,
                      fontSize: "0.9rem",
                      color: "#fff",
                      boxShadow: "0 4px 15px rgba(27, 94, 32, 0.3)",
                      "&:hover": {
                        bgcolor: "#2E7D32",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(27, 94, 32, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    View Buyer Profile
                  </Button>
                </Box>
              </Card>
            </Grow>
          </Grid>

          {/* SELLER CARD */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={700}>
              <Card
                sx={{
                  borderRadius: "24px",
                  overflow: "hidden",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {/* Seller Header */}
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #B71C1C 0%, #F44336 100%)",
                    p: 3,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -25,
                      right: -25,
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.15)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} position="relative" zIndex={1}>
                    <Avatar
                      src={contact.seller?.[0]?.profile_photo}
                      sx={{
                        width: 65,
                        height: 65,
                        border: "4px solid #fff",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                        // animation: `${float} 3s ease-in-out infinite`,
                        bgcolor: "#EF5350",
                      }}
                    >
                      <StoreIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box flex={1}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        {contact.seller?.[0]?.name || "Unknown Seller"}
                      </MDTypography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Chip
                          icon={
                            <BadgeIcon
                              sx={{ color: "#fff !important", fontSize: "14px !important" }}
                            />
                          }
                          label="SELLER"
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "0.7rem",
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        {/* {getStatusChip(contact.seller?.[0]?.status)} */}
                      </Box>
                    </Box>
                    <Tooltip title="View Full Seller Profile" arrow TransitionComponent={Zoom}>
                      <IconButton
                        onClick={() => navigateToUser(contact.seller?.[0]?._id)}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.35)",
                            transform: "rotate(360deg)",
                          },
                          transition: "all 0.5s ease",
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Seller Info */}
                <Box sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    <InfoItem
                      icon={<CorporateFareIcon />}
                      label="Company"
                      value={contact.seller?.[0]?.company_name}
                      color="#C62828"
                    />
                    <InfoItem
                      icon={<EmailIcon />}
                      label="Email Address"
                      value={contact.seller?.[0]?.email_id}
                      color="#C62828"
                    />
                    <InfoItem
                      icon={<PhoneIcon />}
                      label="Mobile Number"
                      value={contact.seller?.[0]?.mobile_number}
                      color="#C62828"
                    />
                    <InfoItem
                      icon={<BusinessIcon />}
                      label="Business Type"
                      value={contact.seller?.[0]?.business_type}
                      color="#C62828"
                    />
                    <InfoItem
                      icon={<LocationOnIcon />}
                      label="Location"
                      value={`${contact.seller?.[0]?.city || ""} ${
                        contact.seller?.[0]?.state ? `, ${contact.seller?.[0]?.state}` : ""
                      }`}
                      color="#C62828"
                    />
                  </Stack>

                  <Divider sx={{ my: 2.5 }} />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigateToUser(contact.seller?.[0]?._id)}
                    startIcon={<VisibilityIcon />}
                    sx={{
                      bgcolor: "#C62828",
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: "bold",
                      py: 1.4,
                      fontSize: "0.9rem",
                      color: "#fff",
                      boxShadow: "0 4px 15px rgba(198, 40, 40, 0.3)",
                      "&:hover": {
                        bgcolor: "#D32F2F",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(198, 40, 40, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    View Seller Profile
                  </Button>
                </Box>
              </Card>
            </Grow>
          </Grid>

          {/* POST CARD */}
          {contact.post?.[0] && (
            <Grid item xs={12}>
              <Grow in={true} timeout={800}>
                <Card
                  sx={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)",
                      p: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -30,
                        right: -30,
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} position="relative" zIndex={1}>
                      <Box
                        // sx={{
                        //   bgcolor: "rgba(255,255,255,0.2)",
                        //   borderRadius: "14px",
                        //   p: 1.2,
                        //   backdropFilter: "blur(10px)",
                        // }}
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "rgba(255,255,255,0.2)",
                          borderRadius: "12px",
                          color: "#fff",
                          mr: 1,
                          boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                        }}
                      >
                        <ArticleIcon sx={{ color: "#fff", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <MDTypography variant="h6" fontWeight="bold" color="white">
                          Related Post
                        </MDTypography>
                        <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                          {contact.post?.[0]?.title}
                        </MDTypography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigateToPost(contact.post?.[0]?._id)}
                      startIcon={<VisibilityIcon />}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "#fff",
                        borderRadius: "25px",
                        textTransform: "none",
                        fontWeight: "bold",
                        backdropFilter: "blur(10px)",
                        px: 3,
                        position: "relative",
                        zIndex: 1,
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.3)",
                        },
                      }}
                    >
                      View Post
                    </Button>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={7}>
                        <Stack spacing={3}>
                          <InfoItem
                            icon={<LabelIcon />}
                            label="Post Title"
                            value={contact.post?.[0]?.title}
                            color="#1565C0"
                          />
                          <InfoItem
                            icon={<DescriptionIcon />}
                            label="Description"
                            value={contact.post?.[0]?.description}
                            color="#1565C0"
                          />
                          <InfoItem
                            icon={<LocationOnIcon />}
                            label="Location"
                            value={contact.post?.[0]?.location}
                            color="#1565C0"
                          />
                          <InfoItem
                            icon={<CalendarTodayIcon />}
                            label="Created Date"
                            value={formatDate(contact.post?.[0]?.createdAt)}
                            color="#1565C0"
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Stack spacing={2}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5,
                              bgcolor: "#E3F2FD",
                              borderRadius: "18px",
                              border: "2px solid #90CAF9",
                              transition: "all 0.3s ease",
                              "&:hover": { transform: "scale(1.02)" },
                            }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                                    borderRadius: "12px",
                                    color: "#fff",
                                    mr: 2,
                                    boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                                  }}
                                >
                                  <AttachMoneyIcon sx={{ color: "#fff", fontSize: 20 }} />
                                </Box>
                                <MDTypography variant="body2" fontWeight="bold" color="#1565C0">
                                  Price
                                </MDTypography>
                              </Box>
                              <MDTypography variant="h4" fontWeight="bold" color="#0D47A1">
                                ₹{contact.post?.[0]?.price}
                              </MDTypography>
                            </Box>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5,
                              bgcolor: "#E8F5E9",
                              borderRadius: "18px",
                              border: "2px solid #A5D6A7",
                              transition: "all 0.3s ease",
                              "&:hover": { transform: "scale(1.02)" },
                            }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    background:
                                      "linear-gradient(135deg, #24833fff 0%, #1e7a3cff 100%)",
                                    borderRadius: "12px",
                                    color: "#fff",
                                    mr: 2,
                                    boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                                  }}
                                >
                                  <StraightenIcon sx={{ color: "#fff", fontSize: 20 }} />
                                </Box>
                                <MDTypography variant="body2" fontWeight="bold" color="#2E7D32">
                                  Quantity
                                </MDTypography>
                              </Box>
                              <MDTypography variant="h4" fontWeight="bold" color="#1B5E20">
                                {contact.post?.[0]?.quantity}{" "}
                                <span style={{ fontSize: "0.6em" }}>
                                  {contact.post?.[0]?.unitType}
                                </span>
                              </MDTypography>
                            </Box>
                          </Paper>

                          <Box textAlign="center">
                            <Chip
                              icon={
                                contact.post?.[0]?.post_type === "sell" ? (
                                  <SellIcon sx={{ color: "#fff !important" }} />
                                ) : (
                                  <ShoppingCartIcon sx={{ color: "#fff !important" }} />
                                )
                              }
                              label={contact.post?.[0]?.post_type?.toUpperCase()}
                              sx={{
                                bgcolor:
                                  contact.post?.[0]?.post_type === "sell" ? "#C62828" : "#2E7D32",
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                px: 3,
                                py: 3,
                                borderRadius: "15px",
                                boxShadow:
                                  contact.post?.[0]?.post_type === "sell"
                                    ? "0 4px 15px rgba(198, 40, 40, 0.4)"
                                    : "0 4px 15px rgba(46, 125, 50, 0.4)",
                              }}
                            />
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>

                    {/* <Box mt={3}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => navigateToPost(contact.post?.[0]?._id)}
                        startIcon={<OpenInNewIcon />}
                        sx={{
                          borderRadius: "14px",
                          textTransform: "none",
                          fontWeight: "bold",
                          borderColor: "#1565C0",
                          color: "#1565C0",
                          borderWidth: "2px",
                          py: 1.3,
                          fontSize: "0.9rem",
                          "&:hover": {
                            borderColor: "#0D47A1",
                            bgcolor: "#E3F2FD",
                            borderWidth: "2px",
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 20px rgba(21, 101, 192, 0.2)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        View Complete Post Details
                      </Button>
                    </Box> */}
                  </Box>
                </Card>
              </Grow>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserContactDetails;
